import React from 'react';
import { Box, Form, Heading, SubmitButton } from '@medely/ui-kit';
import { useWorkExperience } from '../../hooks/useWorkExperience';
import {
  useLocationOptionsForWorkExperience,
  useWorkHistoryForWorkExperience,
} from '../../graphql';
import { createWorkExperienceEditValidation } from '../schema';
import { WorkExperienceForm } from '../WorkExperienceForm';
import { useWorkExperienceMutations } from '../../mutations';
import { FormValuesProps } from '../../interface';
import { FragmentType, graphql, parseFragmentData } from '@mp/graphql/generated';
import { LoadingState } from '@mp/pages/application/components/LoadingState';

const EditWorkExperienceFormModalInfoCurrentUserGql = graphql(`
  fragment EditWorkExperienceFormModalInfoCurrentUser on Account {
    id
    professional {
      professional_positions {
        has_recent_experience
        position {
          display_name
          id
        }
      }
    }
    ...WorkExperienceFormCurrentUser
    ...UseWorkExperienceCurrentUser
  }
`);

type EditWorkExperienceFormInfoProps = {
  currentUser?: FragmentType<typeof EditWorkExperienceFormModalInfoCurrentUserGql>;
  onClose: () => void;
  workHistoryId: number | undefined;
  hideTitle?: boolean;
};

export const EditWorkExperienceFormInfo = ({
  currentUser: inputCurrentUser,
  onClose,
  workHistoryId,
  hideTitle = false,
}: EditWorkExperienceFormInfoProps) => {
  const currentUser = parseFragmentData(
    EditWorkExperienceFormModalInfoCurrentUserGql,
    inputCurrentUser,
  );

  const { workHistories } = useWorkHistoryForWorkExperience();
  const { locationTypesLoading } = useLocationOptionsForWorkExperience();

  const workHistory = workHistories?.find((w) => w.id === workHistoryId);

  const { editWorkExperienceInfo } = useWorkExperienceMutations();

  const { createEditPayload, getInitialWorkExperienceValues, totalYearsOfExperience } =
    useWorkExperience({ currentUser });

  const baseValuesLoading = locationTypesLoading;

  const positionId = workHistory?.position?.id;

  const positionName = workHistory?.position?.display_name;

  // @ts-ignore
  const defaultValues = getInitialWorkExperienceValues(workHistory);

  const handleSubmit = (values: FormValuesProps) => {
    const parsedValues = createEditPayload({
      ...values,
      position_id: positionId,
      id: workHistory?.id,
    });
    editWorkExperienceInfo(parsedValues, {
      onSuccess: () => {
        onClose();
      },
    });
  };

  if (baseValuesLoading) {
    return <LoadingState />;
  }

  return (
    <>
      <Form
        handleSubmit={handleSubmit}
        schema={createWorkExperienceEditValidation(totalYearsOfExperience)}
        defaultValues={defaultValues}
      >
        <Box pb={2}>
          {!hideTitle && (
            <Heading size="s" color="text.primary" testId="edit-work-experience-modal-title">
              Edit {positionName} experience
            </Heading>
          )}
        </Box>
        <WorkExperienceForm
          currentUser={currentUser}
          positionId={positionId}
          hidePositionField={true}
          hideButtonToSummary={true}
        />
        <SubmitButton fullWidth testId="edit-work-experience-modal-save-button">
          Save
        </SubmitButton>
      </Form>
    </>
  );
};
